// Intake-logs portal — client data layer.
//
// Parsed invocation rows are read DIRECTLY from Firestore under the security
// rules (logsRole viewer/admin required). Raw log lines are the one thing that
// must go through a Cloud Function: they live in CloudWatch, contain form PHI,
// and every access is audited server-side — see getIntakeLogEvents in
// functions/index.js.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  setDoc,
  startAfter,
  where,
  QueryConstraint,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { app, firestore } from '../firebase';
import {
  DB_LOG_INVOCATIONS,
  DB_LOGS_AUDIT,
  DB_LOGS_CONFIG,
  DB_LOGS_SYNC,
  FN_GET_INTAKE_LOG_EVENTS,
  FN_SET_LOGS_ROLE,
  FN_SYNC_INTAKE_LOGS_NOW,
  LOGS_CONFIG_DOC,
  LOGS_WORKING_SET_CAP,
} from './constants';
import {
  IntakeLogInvocation,
  LogFilters,
  LogGroupConfig,
  LogsAccessAudit,
  LogsRole,
  LogsSyncState,
  RawLogEvent,
} from './types';

// Categorical equality filters we can push to the server, in priority order.
// We push AT MOST ONE (composite indexes exist for each paired with
// started_at); the rest are applied client-side to the loaded set.
const CATEGORICAL_PRIORITY: Array<'center' | 'lambda' | 'status'> = [
  'center',
  'lambda',
  'status',
];

function pickServerCategorical(
  filters: LogFilters,
): { field: 'center' | 'lambda' | 'status'; value: string } | null {
  for (const field of CATEGORICAL_PRIORITY) {
    const value = filters[field];
    if (typeof value === 'string' && value.length > 0) {
      return { field, value };
    }
  }
  return null;
}

function snapToInvocation(snap: QueryDocumentSnapshot<DocumentData>): IntakeLogInvocation {
  const d = snap.data();
  return {
    id: snap.id,
    lambda: d.lambda ?? '',
    log_group: d.log_group ?? '',
    log_stream: d.log_stream ?? '',
    request_id: d.request_id ?? '',
    center: d.center ?? null,
    session_id: d.session_id ?? null,
    status: d.status ?? null,
    timed_out: d.timed_out ?? false,
    error_summary: d.error_summary ?? null,
    started_at: d.started_at ?? 0,
    ended_at: d.ended_at ?? 0,
    duration_ms: d.duration_ms ?? null,
    billed_ms: d.billed_ms ?? null,
    max_memory_mb: d.max_memory_mb ?? null,
    expires_at: d.expires_at ?? 0,
  };
}

// Apply the filters we did NOT push to the server, in memory.
export function applyClientFilters(
  items: IntakeLogInvocation[],
  filters: LogFilters,
  serverField: 'center' | 'lambda' | 'status' | null = null,
): IntakeLogInvocation[] {
  const q = (filters.search ?? '').trim().toLowerCase();
  return items.filter((r) => {
    for (const field of CATEGORICAL_PRIORITY) {
      if (field === serverField) continue; // already enforced server-side
      const want = filters[field];
      if (typeof want === 'string' && want.length > 0) {
        if ((r as any)[field] !== want) return false;
      }
    }
    if (q) {
      const hay = `${r.session_id ?? ''} ${r.center ?? ''} ${r.error_summary ?? ''} ${r.request_id}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

// Load a bounded working set (optional server categorical + date range),
// newest first, for live client-side filtering. Returns `capped: true` when
// more rows exist — pass the cursor back in to append the next-older chunk.
export async function loadInvocations(opts: {
  filters?: LogFilters;
  cap?: number;
  cursor?: QueryDocumentSnapshot<DocumentData>;
}): Promise<{
  items: IntakeLogInvocation[];
  capped: boolean;
  cursor?: QueryDocumentSnapshot<DocumentData>;
  serverField: 'center' | 'lambda' | 'status' | null;
}> {
  const filters = opts.filters ?? {};
  const cap = Math.max(1, opts.cap ?? LOGS_WORKING_SET_CAP);
  const col = collection(firestore, DB_LOG_INVOCATIONS);

  const constraints: QueryConstraint[] = [];
  const cat = pickServerCategorical(filters);
  if (cat) constraints.push(where(cat.field, '==', cat.value));
  if (typeof filters.date_from === 'number') {
    constraints.push(where('started_at', '>=', filters.date_from));
  }
  if (typeof filters.date_to === 'number') {
    constraints.push(where('started_at', '<=', filters.date_to));
  }
  constraints.push(orderBy('started_at', 'desc'));
  if (opts.cursor) constraints.push(startAfter(opts.cursor));
  constraints.push(fbLimit(cap + 1)); // +1 sentinel to detect capping

  const snap = await getDocs(query(col, ...constraints));
  const capped = snap.docs.length > cap;
  const page = snap.docs.slice(0, cap);
  return {
    items: page.map(snapToInvocation),
    capped,
    cursor: capped ? page[page.length - 1] : undefined,
    serverField: cat ? cat.field : null,
  };
}

// ---------- Sync state / config ----------

export async function listSyncState(): Promise<LogsSyncState[]> {
  const snap = await getDocs(collection(firestore, DB_LOGS_SYNC));
  return snap.docs.map((d) => {
    const x = d.data();
    return {
      key: d.id,
      log_group: x.log_group ?? '',
      last_run_at: x.last_run_at ?? 0,
      last_event_ts: x.last_event_ts ?? null,
      last_scanned: x.last_scanned ?? null,
      last_invocations: x.last_invocations ?? null,
      truncated: x.truncated ?? false,
      last_error: x.last_error ?? null,
    };
  });
}

export async function loadLogGroupsConfig(): Promise<{
  groups: LogGroupConfig[];
  backfillHours: number;
}> {
  const snap = await getDoc(doc(firestore, DB_LOGS_CONFIG, LOGS_CONFIG_DOC));
  const data = snap.exists() ? snap.data() : {};
  const groups = Array.isArray(data.groups)
    ? data.groups.filter((g: any) => g && g.key && g.logGroup)
    : [];
  return {
    groups: groups.map((g: any) => ({
      key: String(g.key),
      label: String(g.label ?? g.key),
      logGroup: String(g.logGroup),
    })),
    backfillHours: Number(data.backfill_hours) > 0 ? Number(data.backfill_hours) : 24,
  };
}

// Admin-only under the rules (isLogsAdmin). merge:false on purpose — the
// admin page owns the whole config shape.
export async function saveLogGroupsConfig(
  groups: LogGroupConfig[],
  backfillHours: number,
): Promise<void> {
  await setDoc(doc(firestore, DB_LOGS_CONFIG, LOGS_CONFIG_DOC), {
    groups,
    backfill_hours: backfillHours,
    updated_at: Date.now(),
  });
}

// ---------- Callables ----------

// Fetch the raw CloudWatch lines for one invocation. The function re-checks
// authorization and writes an audit row before returning anything.
export async function fetchRawLogEvents(
  invocationId: string,
): Promise<{ events: RawLogEvent[]; truncated: boolean }> {
  const functions = getFunctions(app, 'us-central1');
  const callable = httpsCallable<{ invocationId: string }, { events: RawLogEvent[]; truncated: boolean }>(
    functions,
    FN_GET_INTAKE_LOG_EVENTS,
  );
  const res = await callable({ invocationId });
  return res.data;
}

export async function setLogsRole(
  email: string,
  role: LogsRole | null,
): Promise<{ uid: string; email: string; role: LogsRole | null }> {
  const functions = getFunctions(app, 'us-central1');
  const callable = httpsCallable(functions, FN_SET_LOGS_ROLE);
  const res = await callable({ email, role });
  return res.data as { uid: string; email: string; role: LogsRole | null };
}

export interface SyncNowResult {
  results: Array<{
    key: string;
    scanned: number;
    invocations: number;
    truncated?: boolean;
    error?: string;
  }>;
}

export async function syncIntakeLogsNow(): Promise<SyncNowResult> {
  const functions = getFunctions(app, 'us-central1');
  const callable = httpsCallable(functions, FN_SYNC_INTAKE_LOGS_NOW);
  const res = await callable({});
  return res.data as SyncNowResult;
}

// ---------- Admin: audit log ----------
// Admin-only read (enforced by the security rules). Newest first.
export async function listRecentLogsAudit(max = 100): Promise<LogsAccessAudit[]> {
  const col = collection(firestore, DB_LOGS_AUDIT);
  const snap = await getDocs(query(col, orderBy('created_at', 'desc'), fbLimit(max)));
  return snap.docs.map((d) => {
    const x = d.data();
    return {
      id: d.id,
      user_id: x.user_id ?? '',
      email: x.email ?? '',
      action: x.action ?? 'view_raw',
      invocation_id: x.invocation_id ?? '',
      lambda: x.lambda ?? null,
      center: x.center ?? null,
      session_id: x.session_id ?? null,
      ip: x.ip ?? '',
      user_agent: x.user_agent ?? '',
      created_at: x.created_at ?? 0,
    } as LogsAccessAudit;
  });
}
